/**
 * /claude-harness/scorecard — the free Harness Scorecard diagnostic.
 *
 * WHY THIS COSTS NOTHING TO RUN
 * -----------------------------
 * The prompt is a static asset the visitor copies into THEIR OWN assistant. No
 * API call, no auth, no rate limit, no abuse surface, and no inference bill —
 * the evaluation happens on their machine, against their files. That is also
 * what makes the privacy claim true rather than a promise: their configuration
 * never reaches us because there is nothing here to reach.
 *
 * The instrument declares itself non-commercial ("you are not selling anything
 * ... you are a diagnostic instrument"), which is exactly why it converts. So
 * the CTA lives AROUND the output, never inside the prompt.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const PROMPT_URL = "/harness-scorecard.md";

export default function HarnessScorecardPage() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const [prompt, setPrompt] = useState<string>("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Fetched rather than bundled: 25KB of prompt has no business in the JS
    // bundle every visitor downloads, and this way the .md and the on-page copy
    // can never drift — they are the same bytes.
    fetch(PROMPT_URL)
      .then((r) => (r.ok ? r.text() : ""))
      .then(setPrompt)
      .catch(() => setPrompt(""));
  }, []);

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard is permission-gated and blocked outright in some contexts.
      // The download button is the fallback, so failing silently here is fine.
    }
  }

  const steps = [
    t("HarnessScorecardPage.step1"),
    t("HarnessScorecardPage.step2"),
    t("HarnessScorecardPage.step3"),
  ];
  const expectations = [
    t("HarnessScorecardPage.expect1"),
    t("HarnessScorecardPage.expect2"),
    t("HarnessScorecardPage.expect3"),
    t("HarnessScorecardPage.expect4"),
  ];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
      <Button
        component={RouterLink}
        to="/claude-harness"
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 3 }}
      >
        {t("HarnessScorecardPage.back")}
      </Button>

      <Typography
        variant="overline"
        sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.12em" }}
      >
        {t("HarnessScorecardPage.eyebrow")}
      </Typography>
      <Typography variant="h3" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
        {t("HarnessScorecardPage.title")}
      </Typography>
      <Typography variant="h6" fontWeight={400} color="text.secondary" sx={{ maxWidth: 660, lineHeight: 1.55 }}>
        {t("HarnessScorecardPage.lede")}
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={copyPrompt}
          disabled={!prompt}
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
        >
          {copied ? t("HarnessScorecardPage.copiedButton") : t("HarnessScorecardPage.copyButton")}
        </Button>
        <Button
          variant="outlined"
          size="large"
          component="a"
          href={PROMPT_URL}
          download="harness-scorecard.md"
          startIcon={<DownloadIcon />}
        >
          {t("HarnessScorecardPage.downloadButton")}
        </Button>
      </Stack>

      <Typography variant="h5" fontWeight={700} sx={{ mt: 6, mb: 2 }}>
        {t("HarnessScorecardPage.howTitle")}
      </Typography>
      <Stack spacing={1.5}>
        {steps.map((s, i) => (
          <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, minWidth: 22, fontFamily: "monospace" }}
            >
              {i + 1}.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ flex: 1 }}>
              {s}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Alert
        icon={<LockOutlinedIcon fontSize="inherit" />}
        severity="info"
        sx={{ mt: 4 }}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {t("HarnessScorecardPage.privacyTitle")}
        </Typography>
        <Typography variant="body2">{t("HarnessScorecardPage.privacyBody")}</Typography>
      </Alert>

      <Typography variant="h5" fontWeight={700} sx={{ mt: 6, mb: 2 }}>
        {t("HarnessScorecardPage.expectTitle")}
      </Typography>
      <Stack spacing={1}>
        {expectations.map((e, i) => (
          <Typography component="li" key={i} variant="body1" color="text.secondary" sx={{ ml: 3 }}>
            {e}
          </Typography>
        ))}
      </Stack>

      <Paper
        elevation={0}
        sx={{ p: 3, mt: 4, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {t("HarnessScorecardPage.honestTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("HarnessScorecardPage.honestBody")}
        </Typography>
      </Paper>

      <Divider sx={{ my: 6 }} />

      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        {t("HarnessScorecardPage.courseTitle")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5, maxWidth: 620 }}>
        {t("HarnessScorecardPage.courseBody")}
      </Typography>
      <Button
        component={RouterLink}
        to="/claude-harness"
        variant="contained"
        size="large"
        endIcon={<ArrowForwardIcon />}
      >
        {t("HarnessScorecardPage.courseCta")}
      </Button>

      {prompt && (
        <Box sx={{ mt: 7 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            {t("HarnessScorecardPage.promptTitle")}
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              maxHeight: 460,
              overflow: "auto",
            }}
          >
            <Box
              component="pre"
              sx={{
                m: 0,
                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                fontSize: 12.5,
                lineHeight: 1.65,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "text.secondary",
              }}
            >
              {prompt}
            </Box>
          </Paper>
        </Box>
      )}
    </Container>
  );
}
